import { forwardRef } from 'react';
import type { ReactNode } from 'react';
import { Combobox, Option, Field, mergeClasses } from '@fluentui/react-components';
import type { ComboboxProps } from '@fluentui/react-components';
import { useComboBoxStyles } from './ComboBox.styles';

/** A single option for the ComboBox convenience prop. */
export interface ComboBoxOption {
  /** Unique identifier */
  key: string;
  /** Display text */
  text: string;
  /** Whether option is disabled */
  disabled?: boolean;
}

/**
 * Props for GEM ComboBox component.
 * Extends native Fluent UI Combobox props.
 */
export interface ComboBoxProps extends Omit<ComboboxProps, 'children'> {
  /** Label text displayed above the combobox */
  label?: string;
  /** Whether the field is required (shows asterisk) */
  required?: boolean;
  /** Validation message displayed below the combobox */
  validationMessage?: string;
  /** Validation state controlling message styling */
  validationState?: 'error' | 'warning' | 'success' | 'none';
  /** Convenience prop: array of options to render */
  options?: ComboBoxOption[];
  /** Custom children (overrides options if both provided) */
  children?: ReactNode;
}

/**
 * Themed ComboBox — thin wrapper around Fluent UI Combobox + Field.
 * Applies GEM defaults: appearance="outline", size="large".
 * Accepts an `options` array for convenience or custom `children` for advanced rendering.
 */
export const ComboBox = forwardRef<HTMLInputElement, ComboBoxProps>(
  (
    {
      label,
      required,
      validationMessage,
      validationState,
      options,
      children,
      appearance = 'outline',
      size = 'large',
      positioning = {
        position: 'below',
        align: 'start',
        flipBoundary: 'scrollParent',
        overflowBoundary: 'scrollParent',
      },
      className,
      ...rest
    },
    ref
  ) => {
    const styles = useComboBoxStyles();

    const renderedChildren =
      children ??
      options?.map((option) => (
        <Option key={option.key} value={option.key} disabled={option.disabled}>
          {option.text}
        </Option>
      ));

    return (
      <Field
        className={mergeClasses(styles.root, className)}
        label={label}
        required={required}
        validationMessage={validationMessage}
        validationState={validationState}
      >
        <Combobox
          ref={ref}
          appearance={appearance}
          size={size}
          positioning={positioning}
          listbox={{ className: styles.listbox }}
          input={{ autoComplete: 'off', 'data-form-type': 'other' } as never}
          {...rest}
        >
          {renderedChildren}
        </Combobox>
      </Field>
    );
  }
);

ComboBox.displayName = 'ComboBox';
